import React, { useEffect, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash, Calendar as CalendarIcon, Filter } from 'iconoir-react';
import { format } from 'date-fns';

const CHECKLIST_CATEGORIES = [
  'Venue', 'Catering', 'Photography', 'Attire', 'Decor', 
  'Music', 'Invitations', 'Beauty', 'Transportation', 'General',
  'Entertainment', 'Florist', 'Priest', 'Gifts', 'Rentals',
  'Lighting/Sound/Production', 'Wedding Cake/Desserts'
];

function TaskItem({ item, onToggle, onEdit, onDelete, getPriorityBadge, isOverdue }) {
  return (
    <Card className={`card-surface p-3 ${item.completed ? 'opacity-60' : ''}`} data-testid={`task-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={item.completed} onCheckedChange={() => onToggle(item)} className="mt-0.5" data-testid={`task-checkbox-${item.id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h3 className={`font-sans text-sm text-charcoal ${item.completed ? 'line-through' : ''}`}>{item.title}</h3>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <Badge className={`${getPriorityBadge(item.priority)} border text-xs capitalize py-0`}>{item.priority}</Badge>
                <Badge variant="outline" className="text-xs py-0">{item.category}</Badge>
                {item.event_name && !item._grouped && <Badge variant="outline" className="text-xs py-0 bg-page">{item.event_name}</Badge>}
                {item.due_date && (
                  <span className={`text-xs flex items-center gap-1 ${isOverdue(item.due_date) && !item.completed ? 'text-terracotta' : 'text-muted'}`}>
                    <CalendarIcon className="w-3 h-3" />
                    {format(new Date(item.due_date), 'MMM d, yyyy')}
                    {isOverdue(item.due_date) && !item.completed && ' (Overdue)'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => onEdit(item)} className="p-1.5 text-muted hover:text-charcoal transition-colors" data-testid={`edit-task-${item.id}`}>
                <Edit className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => onDelete(item.id)} className="p-1.5 text-muted hover:text-terracotta transition-colors" data-testid={`delete-task-${item.id}`}>
                <Trash className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function ChecklistPage() {
  const { 
    currentWedding, checklist, events,
    fetchChecklist, createChecklistItem, updateChecklistItem, deleteChecklistItem, fetchEvents
  } = useWedding();
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [groupByEvent, setGroupByEvent] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    category: 'General',
    priority: 'medium',
    completed: false
  });

  useEffect(() => {
    if (currentWedding) {
      fetchChecklist(currentWedding.id);
      fetchEvents(currentWedding.id);
    }
  }, [currentWedding, fetchChecklist, fetchEvents]);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        description: item.description || '',
        due_date: item.due_date || '',
        category: item.category || 'General',
        priority: item.priority || 'medium',
        completed: item.completed || false
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        due_date: '',
        category: 'General',
        priority: 'medium',
        completed: false
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemData = {
      wedding_id: currentWedding.id,
      title: formData.title,
      description: formData.description,
      due_date: formData.due_date || null,
      category: formData.category,
      priority: formData.priority,
      completed: formData.completed
    };

    if (editingItem) {
      await updateChecklistItem(editingItem.id, itemData);
    } else {
      await createChecklistItem(itemData);
    }
    setShowModal(false);
    fetchChecklist(currentWedding.id);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      await deleteChecklistItem(itemId);
    }
  };

  const handleToggleComplete = async (item) => {
    await updateChecklistItem(item.id, { completed: !item.completed });
  };

  // Build event order from events list (chronological or creation order)
  const eventOrder = {};
  events.forEach((e, i) => { eventOrder[e.name] = i; });

  // Build counts per event (+ general) for the chip strip (ignoring filters so
  // chips always show the true total available per event)
  const chipCounts = checklist.reduce((acc, item) => {
    const key = item.event_name || 'general';
    acc[key] = (acc[key] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const filteredChecklist = checklist
    .filter(item => {
      if (!showCompleted && item.completed) return false;
      if (filterCategory !== 'all' && item.category !== filterCategory) return false;
      if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
      if (filterEvent !== 'all') {
        if (filterEvent === 'general') {
          if (item.event_name) return false;
        } else if (item.event_name !== filterEvent) return false;
      }
      return true;
    })
    .sort((a, b) => {
      // 1. Completed items last
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      // 2. Sort by event order (General first = -1, then events in order)
      const aEvtOrder = a.event_name ? (eventOrder[a.event_name] ?? 999) : -1;
      const bEvtOrder = b.event_name ? (eventOrder[b.event_name] ?? 999) : -1;
      if (aEvtOrder !== bEvtOrder) return aEvtOrder - bEvtOrder;
      // 3. Within same event: high → medium → low priority
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
    });

  // Group tasks by event - maintain event order
  const groupedChecklist = {};
  if (groupByEvent) {
    // First add General group
    const generalItems = filteredChecklist.filter(i => !i.event_name);
    if (generalItems.length > 0) groupedChecklist['General'] = generalItems;
    // Then add event groups in order
    events.forEach(event => {
      const items = filteredChecklist.filter(i => i.event_name === event.name);
      if (items.length > 0) groupedChecklist[event.name] = items;
    });
    // Any items with event_name not matching known events
    const knownNames = new Set(['General', ...events.map(e => e.name)]);
    filteredChecklist.forEach(item => {
      const group = item.event_name || 'General';
      if (!knownNames.has(group)) {
        if (!groupedChecklist[group]) groupedChecklist[group] = [];
        groupedChecklist[group].push(item);
      }
    });
  }

  // Get unique event names for filter (in event order)
  const eventNames = events.map(e => e.name).filter(name => 
    checklist.some(i => i.event_name === name)
  );

  const totalTasks = checklist.length;
  const completedTasks = checklist.filter(i => i.completed).length;
  const highPriorityPending = checklist.filter(i => !i.completed && i.priority === 'high').length;

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-terracotta/10 text-terracotta border-terracotta/30',
      medium: 'bg-ochre/10 text-ochre border-ochre/30',
      low: 'bg-moss/10 text-moss border-moss/30'
    };
    return styles[priority] || styles.medium;
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">
          Please select or create a wedding first.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="checklist-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
              Checklist
            </h1>
            <p className="text-muted font-light mt-2">
              Track your wedding planning tasks
            </p>
          </div>
          <Button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-task-btn"
          >
            <Plus className="w-4 h-4" />
            Add Task
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Tasks</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="total-tasks">
              {totalTasks}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Completed</p>
            <p className="font-mono text-2xl text-moss tracking-tight" data-testid="completed-tasks">
              {completedTasks}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Pending</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="pending-tasks">
              {totalTasks - completedTasks}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">High Priority</p>
            <p className="font-mono text-2xl text-terracotta tracking-tight" data-testid="high-priority-tasks">
              {highPriorityPending}
            </p>
          </Card>
        </div>

        {/* Event Filter Chip Strip — cross-event navigation for fusion weddings */}
        {(eventNames.length > 0 || chipCounts.general) && (
          <div className="flex flex-wrap gap-2 items-center" data-testid="checklist-event-chips">
            {[
              { key: 'all',     label: 'All tasks',  count: chipCounts.all || 0 },
              ...(chipCounts.general ? [{ key: 'general', label: 'General',   count: chipCounts.general }] : []),
              ...eventNames.map((name) => ({ key: name, label: name, count: chipCounts[name] || 0 })),
            ].map((chip) => {
              const active = filterEvent === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setFilterEvent(chip.key)}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-sans transition-all duration-200 border ${
                    active
                      ? 'bg-terracotta text-ivory border-terracotta shadow-sm'
                      : 'bg-white text-charcoal border-border hover:border-terracotta hover:text-terracotta'
                  }`}
                  data-testid={`chip-${chip.key.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  aria-pressed={active}
                >
                  <span>{chip.label}</span>
                  <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                    active ? 'bg-ivory/20 text-ivory' : 'bg-page text-muted'
                  }`}>
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <Card className="card-surface p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-4 h-4 text-muted" />
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">Category:</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-32 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CHECKLIST_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">Priority:</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-28 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="showCompleted"
                checked={showCompleted}
                onCheckedChange={setShowCompleted}
              />
              <Label htmlFor="showCompleted" className="text-sm text-muted cursor-pointer">
                Show completed
              </Label>
            </div>
            {eventNames.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="groupByEvent"
                  checked={groupByEvent}
                  onCheckedChange={setGroupByEvent}
                />
                <Label htmlFor="groupByEvent" className="text-sm text-muted cursor-pointer">
                  Group by event
                </Label>
              </div>
            )}
          </div>
        </Card>

        {/* Checklist Items */}
        {filteredChecklist.length === 0 ? (
          <Card className="card-surface p-12 text-center">
            <p className="text-muted mb-4">No tasks found</p>
            <Button 
              onClick={() => handleOpenModal()}
              className="btn-primary"
              data-testid="create-first-task-btn"
            >
              Create Your First Task
            </Button>
          </Card>
        ) : groupByEvent && Object.keys(groupedChecklist).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedChecklist).map(([eventName, items]) => {
              const eventCompleted = items.filter(i => i.completed).length;
              return (
                <div key={eventName}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-serif text-xl text-charcoal">{eventName}</h2>
                    <span className="text-xs text-muted font-mono">{eventCompleted}/{items.length} done</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((item) => (
                      <TaskItem key={item.id} item={item} onToggle={handleToggleComplete} onEdit={handleOpenModal} onDelete={handleDelete} getPriorityBadge={getPriorityBadge} isOverdue={isOverdue} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredChecklist.map((item) => (
              <TaskItem key={item.id} item={item} onToggle={handleToggleComplete} onEdit={handleOpenModal} onDelete={handleDelete} getPriorityBadge={getPriorityBadge} isOverdue={isOverdue} />
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingItem ? 'Edit Task' : 'Add New Task'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-field h-12"
                  placeholder="Task title"
                  required
                  data-testid="task-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field h-12"
                  placeholder="Optional description"
                  data-testid="task-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="input-field h-12" data-testid="task-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHECKLIST_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger className="input-field h-12" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="input-field h-12"
                  data-testid="task-due-date-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  data-testid="save-task-btn"
                >
                  {editingItem ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
